import * as THREE from 'three';
import { readSurface, standsOn } from '../engine/placement.js';

/** Where the laptop room is looked down on from: above any table. */
const LOOK_FROM = 1.3;

/**
 * How far something may have moved from where the last answer was found and
 * still use it. A hit-test answers a frame or two after it is asked, so while
 * a skeleton is being dragged the answer trails it; it settles once it slows.
 */
const STILL_TRUE = 0.1;

/** How far the spot must move before the headset looks again at once. */
const LOOK_AGAIN = 0.03;

/**
 * How much the guessed height must change to look again at once. Hits on one
 * table top differ by fractions of a millimetre; without this every one of
 * them counted as a new guess, and a new line of sight was asked for every
 * frame and a half instead of every ten.
 */
const NEW_GUESS = 0.01;

/**
 * How often the headset looks again at a spot that has not moved, in frames:
 * the line of sight is fixed to the head when it is asked for, so as the head
 * moves an old one points somewhere else.
 */
const REFRESH_FRAMES = 10;

type Xr = {
  getSession(): XRSession | null;
  getReferenceSpace(): XRReferenceSpace | null;
};

type Look = { x: number; z: number; height: number; hits: XRHitTestSource };

/**
 * The height of the table top or floor below a point, so a skeleton moved by
 * its handle stands on it instead of keeping whatever height it had.
 *
 * In a headset: the Quest's hit-test, which answers only along a line of sight
 * from the eyes. A ray straight down from above the skeleton, or any ray fixed
 * in the room, got nothing on Pieter's Quest in 7,500 frames; a ray from the
 * eye got a hit every frame. So it looks from the eye at the spot beneath the
 * feet, at a guessed height, and aims again at whatever height it meets until
 * the line of sight lands on the spot (`readSurface`). Only what is in view
 * can be found, which the skeleton is while a student is moving it.
 *
 * On a laptop: a ray straight down into the virtual room (DemoRoom).
 */
export class Surfaces {
  #session: XRSession | undefined;
  #viewer: XRReferenceSpace | undefined;
  #eye: { at: THREE.Vector3; turn: THREE.Quaternion } | undefined;
  #want: { x: number; z: number } | undefined;
  #look: Look | undefined;
  #asking = false;
  #age = 0;
  #guess = 0;
  #found: { x: number; z: number; y: number } | undefined;
  readonly #ray = new THREE.Raycaster();

  constructor(
    private readonly xr: Xr,
    private readonly room: readonly THREE.Object3D[],
  ) {}

  /** The height to stand on at (x, z), or nothing if it is not known yet. */
  below(x: number, z: number): number | undefined {
    const session = this.xr.getSession();
    if (!session) return this.#inRoom(x, z);

    this.#start(session);
    this.#want = { x, z };
    const found = this.#found;
    return found && Math.hypot(found.x - x, found.z - z) <= STILL_TRUE ? found.y : undefined;
  }

  /** Reads this frame's answer and looks again if it is time. Once a frame. */
  update(frame?: XRFrame): void {
    const reference = this.xr.getReferenceSpace();
    if (!frame || !reference) return;

    const viewer = frame.getViewerPose(reference);
    if (viewer) {
      const { position: p, orientation: q } = viewer.transform;
      this.#eye = {
        at: new THREE.Vector3(p.x, p.y, p.z),
        turn: new THREE.Quaternion(q.x, q.y, q.z, q.w),
      };
    }

    const look = this.#look;
    if (look) {
      const pose = frame.getHitTestResults(look.hits)[0]?.getPose(reference);
      if (pose) {
        const m = pose.transform.matrix;
        // The pose's y axis is the surface's normal; its y component is how
        // much the surface faces upwards.
        const hit = { x: m[12] ?? 0, y: m[13] ?? 0, z: m[14] ?? 0, up: m[5] ?? 0 };
        const read = readSurface(look, hit);
        if (read && 'standsAt' in read) {
          this.#found = { x: look.x, z: look.z, y: read.standsAt };
          this.#guess = read.standsAt;
        } else if (read) {
          this.#guess = read.tryHeight;
        }
      }
    }

    this.#age++;
    this.#lookAgain();
  }

  dispose(): void {
    this.#look?.hits.cancel();
    this.#look = undefined;
  }

  #start(session: XRSession): void {
    if (session === this.#session) return;
    // A new session: whatever was known belonged to the last one.
    this.#session = session;
    this.#viewer = undefined;
    this.#look = undefined;
    this.#found = undefined;
    void session
      .requestReferenceSpace('viewer')
      .then((viewer) => {
        this.#viewer = viewer;
      })
      .catch(() => undefined);
  }

  /** Asks for a line of sight from the eye to the spot, at the guessed height. */
  #lookAgain(): void {
    const session = this.#session;
    const viewer = this.#viewer;
    const eye = this.#eye;
    const want = this.#want;
    if (this.#asking || !session?.requestHitTestSource || !viewer || !eye || !want) return;

    const look = this.#look;
    const moved = !look || Math.hypot(look.x - want.x, look.z - want.z) >= LOOK_AGAIN;
    const newGuess = look !== undefined && Math.abs(look.height - this.#guess) > NEW_GUESS;
    if (!moved && !newGuess && this.#age < REFRESH_FRAMES) return;

    const height = this.#guess;
    const direction = new THREE.Vector3(want.x, height, want.z)
      .sub(eye.at)
      .normalize()
      // In the head's own space: the Quest answers only there.
      .applyQuaternion(eye.turn.clone().invert());

    this.#asking = true;
    this.#age = 0;
    const { x, z } = want;
    session
      .requestHitTestSource({
        space: viewer,
        offsetRay: new XRRay(
          { x: 0, y: 0, z: 0, w: 1 },
          { x: direction.x, y: direction.y, z: direction.z, w: 0 },
        ),
      })
      ?.then((hits) => {
        this.#look?.hits.cancel();
        this.#look = { x, z, height, hits };
      })
      .catch(() => undefined)
      .finally(() => {
        this.#asking = false;
      });
  }

  #inRoom(x: number, z: number): number | undefined {
    this.#ray.set(new THREE.Vector3(x, LOOK_FROM, z), new THREE.Vector3(0, -1, 0));
    const hit = this.#ray.intersectObjects([...this.room], false)[0];
    if (!hit?.face) return undefined;
    const up = hit.face.normal.clone().transformDirection(hit.object.matrixWorld).y;
    return standsOn({ y: hit.point.y, up }) ? hit.point.y : undefined;
  }
}
